<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useToast } from 'primevue/usetoast'

// PrimeVue components
import { default as PInputText } from 'primevue/inputtext'
import { default as PSelect } from 'primevue/select'
import { default as PSkeleton } from 'primevue/skeleton'
import { default as PMessage } from 'primevue/message'
import { default as PProgressBar } from 'primevue/progressbar'

// Custom components
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import CLearningCourseHeader from '@/presentation/components/learning/course/CLearningCourseHeader.vue'

// Stores
import { useLearningCoursesStore } from '@/presentation/stores/learning/learningCourses'
import { useReferentialStore } from '@/presentation/stores/referential/referential'

// Domain models & enums
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus'
import { LearningCourseInput } from '@/core/domain/learning/course/LearningCourse'

// SETUP
const router = useRouter()
const toast = useToast()

// STORES
const learningCoursesStore = useLearningCoursesStore()
const referentialStore = useReferentialStore()

// STORE REFS
const { learningCourses, loading, error } = storeToRefs(learningCoursesStore)
const { referential } = storeToRefs(referentialStore)

// COMPUTED
const isLoading = computed(() => loading.value.fetchAllCourses || loading.value.createCourse)
const isSaving = computed(() => loading.value.createCourse)
const hasError = computed(() => error.value && error.value.hasError)
const errorMessage = computed(() => (error.value?.hasError ? error.value.message : ''))

// LOCAL STATE
const formData = ref({
  title: '',
  domainId: null as number | null,
  status: LearningStatus.Values.DRAFT
})

const selectedAxis = ref<number | null>(null)
const formSubmitted = ref(false)
const validationErrors = ref<Record<string, string>>({})

// COMPUTED
const domainOptions = computed(() => {
  if (!selectedAxis.value) return []

  const axis = referential.value.find((a) => a.id === selectedAxis.value)
  if (!axis?.domains) return []

  return axis.domains.map((domain) => ({
    label: domain.title,
    value: domain.id
  }))
})

const axisOptions = computed(() => {
  return referential.value.map((axis) => ({
    label: axis.title,
    value: axis.id
  }))
})

const isFormValid = computed(() => {
  return formData.value.title && formData.value.domainId
})

// Fonction pour générer un slug depuis le titre
function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Retirer les accents
    .replace(/[^a-z0-9]+/g, '-') // Remplacer les caractères non-alphanumériques par des tirets
    .replace(/^-+|-+$/g, '') // Retirer les tirets en début et fin
}

// LIFECYCLE HOOKS
onMounted(async () => {
  try {
    // Fetch referential data for axes and domains
    await referentialStore.fetchAllAxes()

    // Fetch existing courses
    await learningCoursesStore.fetchAllCourses()
  } catch (error) {
    console.error('Erreur lors du chargement initial des données:', error)
  }
})

// METHODS
async function saveCourse() {
  formSubmitted.value = true

  // Validate form
  validationErrors.value = {}
  if (!formData.value.title) validationErrors.value.title = 'Le nom du parcours est obligatoire'
  if (!formData.value.domainId) validationErrors.value.domainId = 'Le domaine est obligatoire'

  if (!isFormValid.value) {
    return
  }

  try {
    // Générer automatiquement le nom depuis le titre
    const generatedName = generateSlug(formData.value.title)

    // Create course using new architecture
    const newCourse = await learningCoursesStore.createCourse({
      title: formData.value.title,
      name: generatedName,
      domainId: formData.value.domainId!,
      status: formData.value.status,
      order: learningCourses.value.length + 1
    })

    // Show success message
    toast.add({
      severity: 'success',
      summary: 'Parcours créé',
      detail: `Le parcours "${newCourse.title}" a été créé avec succès.`,
      life: 3000
    })

    // Navigate back to list
    await router.push({ name: 'learning-courses-list' })
  } catch (error) {
    console.error('Erreur lors de la création du parcours:', error)
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Une erreur est survenue lors de la création du parcours.',
      life: 5000
    })
  }
}

function cancel() {
  router.push({ name: 'learning-courses-list' })
}

function getValidationClass(field: string) {
  if (!formSubmitted.value) return ''
  return validationErrors.value[field] ? 'p-invalid' : ''
}
</script>

<template>
  <c-learning-course-header
    title="Créer un nouveau parcours"
    :primary-button="{
      label: 'Enregistrer',
      onClick: saveCourse,
      icon: 'pi pi-save',
      disabled: isSaving || !isFormValid
    }"
    :secondary-button="{
      label: 'Annuler',
      onClick: cancel,
      icon: 'pi pi-times'
    }"
    :back-to="{
      label: 'Revenir à la liste des parcours',
      route: { name: 'learning-courses-list' }
    }"
  />

  <div class="col-span-12 flex-1 gap-8 overflow-y-auto p-8 pt-2">
    <!-- Message d'erreur global -->
    <p-message v-if="hasError" severity="error" class="mb-4 w-full">
      {{ errorMessage }}
    </p-message>

    <!-- Indicateur de chargement -->
    <p-progress-bar v-if="isSaving" mode="indeterminate" class="mb-4" />

    <!-- Formulaire -->
    <c-base-block title="Informations du parcours">
      <!-- État de chargement -->
      <div v-if="isLoading && !isSaving" class="flex flex-col gap-4">
        <p-skeleton height="2.5rem" />
        <p-skeleton height="2.5rem" />
        <p-skeleton height="2.5rem" />
        <p-skeleton height="2.5rem" />
      </div>

      <!-- Formulaire -->
      <form v-else class="flex flex-col gap-8 lg:w-1/2" @submit.prevent="saveCourse">
        <!-- Nom du parcours -->
        <div class="flex flex-col gap-2">
          <label for="course-title" class="font-medium"> Nom du parcours* </label>
          <p-input-text
            id="course-title"
            v-model="formData.title"
            placeholder="Nom du parcours"
            :class="getValidationClass('title')"
            type="text"
            autocomplete="off"
            data-form-type="other"
            aria-required="true"
          />
          <small v-if="formSubmitted && validationErrors.title" class="p-error">
            {{ validationErrors.title }}
          </small>
        </div>

        <!-- Axe -->
        <div class="flex flex-col gap-2">
          <label for="axis" class="font-medium"> Axe* </label>
          <p-select
            id="axis"
            v-model="selectedAxis"
            :options="axisOptions"
            option-label="label"
            option-value="value"
            placeholder="Sélectionner un axe"
            class="w-full"
          />
        </div>

        <!-- Domaine -->
        <div class="flex flex-col gap-2">
          <label for="domain" class="font-medium"> Domaine* </label>
          <p-select
            id="domain"
            v-model="formData.domainId"
            :options="domainOptions"
            :disabled="!selectedAxis"
            :class="getValidationClass('domainId')"
            option-label="label"
            option-value="value"
            placeholder="Sélectionner un domaine"
            class="w-full"
          />
          <small v-if="formSubmitted && validationErrors.domainId" class="p-error">
            {{ validationErrors.domainId }}
          </small>
          <small v-else-if="!selectedAxis" class="text-surface-600">
            Veuillez d'abord sélectionner un axe
          </small>
        </div>
      </form>
    </c-base-block>
  </div>
</template>
